import React, { memo, useEffect, useCallback, useState} from 'react';
import TrackThumbnail from "components/TrackThumbnail.js";
import {isNumber} from 'utils/IsNumber.js';
import {toggleStep} from 'operations/actions.js';
import {setTrackerParameterToEdit} from 'actions/index.js';
import {getSteps} from './selector.js';
import Notes from 'midi/Notes.js';
import {connect} from 'react-redux';
import Colors from 'components/Colors.js';
import StepData from "pattern/StepData.js";
const colors = new Colors();
const DEFAULT_STEP_DATA = new StepData();
const NUM_STEPS = 7;

const Tracker = ({
    track,
    pattern,
    currentStep,
    step,
    parameterToEdit,
    trackSteps,
    isSelectedTrack,
    cursorStep,
    editing,
    setParameterToEdit}) => { 
        let [color, setColor] = useState(undefined);
        let [value, setValue] = useState(parameterToEdit && parameterToEdit.get());

        useEffect(() => {
            if (parameterToEdit) {
                setValue(parameterToEdit.get());
            }
        }, [setValue, parameterToEdit]);

    useEffect(() => {
        setColor(colors.getColorForTrack(track));
    }, [track, setColor]);

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [parameterToEdit, setValue, track]);

    const onKeyDown = useCallback((e) => {
        if (!parameterToEdit || parameterToEdit.track !== track) {
            return;
        }

        e.stopPropagation();

        let value  = parameterToEdit.get() ||
            track.trackData.parameters.find(x => x.name === parameterToEdit.name).get();

        if (isNumber(e.key)) {
            let number = parseInt(e.key);
            parameterToEdit.set(number);
            setValue(number);
        } else if (e.key >= 'a' && e.key <= 'g') {
            if (parameterToEdit.name === "Transpose") {
                let note = Notes.fromTranspose(parameterToEdit.get());
                note.note = e.key.toUpperCase();
                parameterToEdit.set(note.getTranspose());
                setValue(note.getTranspose());
            }
        } else if (e.key === "ArrowUp") {
            parameterToEdit.set(value + 1);
            setValue(value + 1);
        } else if (e.key === "ArrowDown") {
            parameterToEdit.set(value - 1);
            setValue(value - 1);
        }

        // lets you edit
    }, [parameterToEdit, setValue, track]);

        let length = pattern.length() / 2.0;
        let steps = getSteps(pattern, (!isNaN(step) ? step : currentStep) % length);
    return (
        <div className="tracker">
          <TrackThumbnail hasInner={true} track={track}/>
          {steps.map((data, i) =>
                     <TrackerStep
                       parameterToEdit={parameterToEdit}
                       setParameterToEdit={setParameterToEdit}
                       track={track}
                       on={trackSteps && trackSteps[data.stepNumber]}
                       value={value}
                       key={i+'.'+data.stepNumber}
                       color={color}
                       editing={editing}
                       isCursor={isSelectedTrack && data.stepNumber === cursorStep}
                       isCurrent={data.stepNumber === currentStep % length}
                       data={data}/>)}
        </div>);
}

const TrackerStep = ({track, value, editing, data, isCursor, isCurrent, color, parameterToEdit, setParameterToEdit}) => {
    let {stepData, stepNumber} = data;

    if (!stepData) {
        stepData = DEFAULT_STEP_DATA;
    }

    const onClick= useCallback((e) => {
        track.sequencerEngine.operationManager.dispatch(
            toggleStep(track, stepNumber*2)); 
    }, [stepNumber, track]);

    const onClickParameter = useCallback((e, parameter) => {
        setParameterToEdit(parameter);
        editing.current = true;
        track.sequencerEngine.select(
            track.trackNumber,
            stepNumber);
    }, [stepNumber, track]);

    let className = "tracker-step";
    if (isCurrent) {
        className += " current";
    }

    if (isCursor) {
        className += " cursor";
    }

    return (
        <div
          onClick={stepData && !stepData.on ? onClick : () => []}
          className={className}>
          <div className="inner-step">
          <div
            onClick={onClick}
            className={
                stepNumber % 8 === 0 ? "bar step-number" :
                    stepNumber % 4 === 0 ? "half-bar step-number" : "step-number"}>
            {stepNumber + 1}</div>
          {stepData && stepData.on ?
           <div className="step-data">
             <div
               style={{color: color}}
               onClick={(e) => onClickParameter(e, stepData.transposeParameter)}
               className={parameterToEdit === stepData.transposeParameter ? "edit note-name" : "note-name"}>
               {Notes.fromTranspose(stepData.transpose).toString()}
             </div>
             <TrackerParameter
               v={value}
               value={stepData.duration}
               parameter={stepData.durationParameter}
               parameterToEdit={parameterToEdit}
               setParameterToEdit={onClickParameter}
             />
             <TrackerParameter
               v={value}
               value={stepData.velocity}
               parameter={stepData.velocityParameter}
               parameterToEdit={parameterToEdit}
               setParameterToEdit={onClickParameter}
             />
           </div> :
           ''}
          </div>
        </div>
    );
};

const TrackerParameter = ({value, parameter, setParameterToEdit, parameterToEdit}) => {
    return (
        <div
          onClick={(e) => setParameterToEdit(e, parameter)}
          className={parameter === parameterToEdit ? "edit tracker-parameter" : "tracker-parameter"}>
          {value}
        </div>
    );
};

const mapStateToProps = (state, props) => {
    let cursor = props.track.sequencerEngine.cursor;
    let parameterToEdit = state.mix.trackerParameterToEdit;
    return {
        trackSteps: state.tracks.trackSteps[props.track.id],
        stepBuckets: props.pattern.stepBuckets,
        parameterToEdit,
        isSelectedTrack: cursor.track === props.track.trackNumber
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setParameterToEdit: (param) => dispatch(setTrackerParameterToEdit(param))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Tracker);

